import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { PatientMeta } from '../../models/view/patient-meta';
import { filter, merge, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { UnsubscribeOnDestroy } from '../../../../utils/UnsubscribeOnDestroy';
import { EmitOnDestroy$ } from '../../../../utils/EmitOnDestroy';
import { PatientDetails } from '../../models/dto/patient-details';
import { DatePipe, NgClass, NgForOf } from '@angular/common';
import { isEqual } from 'lodash-es';
import { ViewStudy } from '../../models/view/view-study';
import { SendingStatus } from '../../services/dicom-upload/sending-status';
import { ViewSendingStatus } from '../../models/view/view-sending-status';
import { MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SeriesComponent } from '../series/series.component';
import { FeedbackComponent } from '../feedback/feedback.component';
import { MatButton, MatIconButton } from '@angular/material/button';

class SimplePatientMeta {
    name: string;
    sex: string;
    birthDate: string;

    constructor(name: string, sex: string, birthDate: string) {
        this.name = name;
        this.sex = sex;
        this.birthDate = birthDate;
    }
}

@Component({
    selector: 'study',
    templateUrl: './study.component.html',
    styleUrls: ['./study.component.scss'],
    imports: [
        MatExpansionPanel,
        MatCheckbox,
        FormsModule,
        MatIcon,
        DatePipe,
        TranslatePipe,
        NgForOf,
        NgClass,
        SeriesComponent,
        FeedbackComponent,
        MatExpansionPanelHeader,
        MatIconButton
    ],
    standalone: true,
})
export class StudyComponent implements OnInit, OnChanges, OnDestroy {
    @Input() study!: ViewStudy;
    @Input() check!: Observable<boolean>;
    @Input() patientToMatch!: PatientDetails;
    @Input() sending!: SendingStatus[] | undefined;

    @Output() checked: EventEmitter<boolean>;
    @Output() differentPatient: EventEmitter<void>;
    @Output() delete: EventEmitter<void>;
    joinedModalities: string;
    @EmitOnDestroy$()
    private destroy$!: Subject<void>;
    private toggle$: Subject<boolean>;
    @UnsubscribeOnDestroy()
    private sendingOnChangeSubscription?: Subscription;

    constructor(readonly datePipe: DatePipe) {
        this.checked = new EventEmitter<boolean>();
        this.differentPatient = new EventEmitter<void>();
        this.delete = new EventEmitter<void>();
        this.toggle$ = new Subject<boolean>();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sending']) {
            if (this.sendingOnChangeSubscription) {
                this.sendingOnChangeSubscription.unsubscribe();
            }
            if (this.sending) {
                /*this.sendingOnChangeSubscription = this.sending?.subscribe((r: SendingStatus[]) => this.updateStatusForSending(r));*/
                this.updateStatusForSending(this.sending);
            }
        }
    }

    ngOnInit(): void {
        this.checkPatientEquality();
        merge(this.check, this.toggle$)
            .pipe(
                filter((b: boolean) => this.study.status.selected !== b),
                tap((b: boolean) => (this.study.status.selected = b)),
                takeUntil(this.destroy$),
            )
            .subscribe((b) => {
                this.checked.emit(b);
            });
        this.study.status.selected = !this.study.status.selected;
        this.toggle$.next(!this.study.status.selected);
        this.joinedModalities = [...this.study.viewModality].join('\\');
    }

    ngOnDestroy(): void {
        console.log('distruggo Study component');
    }

    toggle() {
        this.toggle$.next(!this.study.status.selected);
    }

    preventAccordionClick(event: Event) {
        event.stopPropagation();
    }

    onDeleteStudy() {
        this.delete.emit();
    }

    onDelete(event: Event) {
        this.preventAccordionClick(event);
        this.delete.emit();
    }

    private updateStatusForSending(r: SendingStatus[]) {
        if (!r) {
            this.study.status.sendingStatus = ViewSendingStatus.ERROR;
        }
        const status = r.reduce(
            (acc: SendingStatus, response: SendingStatus): SendingStatus => {
                return {
                    httpError: acc.httpError || response.httpError,
                    instanceFailure: acc.instanceFailure || response.instanceFailure,
                    sent: acc.sent && response.sent,
                };
            },
            new SendingStatus(false, false, true),
        );
        // console.log(`Study ${this.study.data.patient.name}: `, status);
        this.study.status = {
            ...this.study.status,
            sendingStatus: status.sent && !status.instanceFailure ? ViewSendingStatus.SENT : ViewSendingStatus.ERROR,
        };
    }

    private checkPatientEquality() {
        const patientToMatch = this.getSimplePatientMetaFromPatientDetails(this.patientToMatch);
        const myPatient = this.getSimplePatientMetaFromPatientMeta(this.study.data.patient);
        this.study.status.patientNotEqual = !isEqual(myPatient, patientToMatch);
        if (this.study.status.patientNotEqual) {
            this.differentPatient.emit();
        }
    }

    private getSimplePatientMetaFromPatientDetails(patientInfo: PatientDetails): SimplePatientMeta {
        return {
            name: patientInfo.Surname.trim() + patientInfo.Name.trim(),
            sex: patientInfo.Sex.trim(),
            birthDate: patientInfo.BirthDate.trim(),
        };
    }

    private getSimplePatientMetaFromPatientMeta(patient: PatientMeta): SimplePatientMeta {
        return {
            name: patient.name.trim().replace(/\s/g, ''),
            sex: patient.sex.trim(),
            birthDate: this.datePipe.transform(patient.birthDate, 'yyyy-MM-dd') || '',
        };
    }
}
